import { CustomSegmented } from "Components/CustomSegmented";
import { ModalComponentV1 } from "Components/ModalComponentV1";
import { useContext, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import grenChecked from "assets/svg/CheckCircleAllGreen.svg";
import warningIcon from "assets/svg/WarningLight.svg";
import styles from "./index.module.css";
import { CustomTextAreaV2 } from "Components/CustomTextAreaV2";
import { CustomSwitcher } from "Components/CustomSwitcher";
import type { UploadFile } from "antd";
import { ReportUpload } from "./RepotUpload";
import { CustomButton } from "Components/CustomButton";
import { useRequest } from "ahooks";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import {
  createDebateReport,
  generateDebateReport,
  getDebateReport,
  getDebateReportPhoto,
  updateDebateReport,
  type DebateReportForm,
  type ReportSectionKey,
} from "helpers/backend_helper";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  debateId: string;
};

type TabKey = 0 | 1 | 2 | 3;

export type FileDataType = {
  filePath: string;
  originalName: string;
};

export type BunchFilesType = {
  files: FileDataType[];
  status: "DONE" | "LOADING" | "ERROR" | "READY";
};

type TabData = {
  text: string;
  files: UploadFile[];
  completed: boolean;
  bunchFiles: BunchFilesType;
};

type TabSetters = {
  setFileList: Dispatch<SetStateAction<UploadFile[]>>;
  setBunchFiles: Dispatch<SetStateAction<BunchFilesType>>;
};

const TAB_KEYS: TabKey[] = [0, 1, 2, 3];

const TAB_NAMES: Record<TabKey, string> = {
  0: "Lessor",
  1: "Renter",
  2: "Additional",
  3: "Resolution",
};

const TAB_SECTIONS: Record<TabKey, ReportSectionKey> = {
  0: "lessor",
  1: "renter",
  2: "additional",
  3: "resolution",
};

const getOptsReport = (choosed?: number[]) => [
  {
    value: 0,
    label: (
      <div className={styles.typeReportWrapper}>
        {choosed?.includes(0) && <img src={grenChecked} alt="checked" />}
        <span>Lessor</span>
      </div>
    ),
  },
  {
    value: 1,
    label: (
      <div className={styles.typeReportWrapper}>
        {choosed?.includes(1) && <img src={grenChecked} alt="checked" />}
        <span>Renter</span>
      </div>
    ),
  },
  {
    value: 2,
    label: (
      <div className={styles.typeReportWrapper}>
        {choosed?.includes(2) && <img src={grenChecked} alt="checked" />}
        <span>Additional</span>
      </div>
    ),
  },
  {
    value: 3,
    label: (
      <div className={styles.typeReportWrapper}>
        {choosed?.includes(3) && <img src={grenChecked} alt="checked" />}
        <span>Resolution</span>
      </div>
    ),
  },
];

const emptyTabData = (): TabData => ({
  text: "",
  files: [],
  completed: false,
  bunchFiles: { files: [], status: "READY" },
});

export const ModalReport = ({ isOpen, onClose, debateId }: Props) => {
  const { localCountry } = useContext(AdminContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const countryIso = localCountry?.iso?.toUpperCase();
  const hasSavedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<TabKey>(0);
  const [tabsData, setTabsData] = useState<Record<TabKey, TabData>>({
    0: emptyTabData(),
    1: emptyTabData(),
    2: emptyTabData(),
    3: emptyTabData(),
  });

  const filledTabs = TAB_KEYS.filter((k) => tabsData[k].completed);
  const options = getOptsReport(filledTabs);

  // Секцию нельзя пометить завершённой без текста, фото опциональны.
  const [textErrorTabs, setTextErrorTabs] = useState<TabKey[]>([]);

  const updateText = (tab: TabKey, text: string) => {
    if (text.trim()) setTextErrorTabs((prev) => prev.filter((t) => t !== tab));
    setTabsData((prev) => ({
      ...prev,
      [tab]: { ...prev[tab], text, completed: text.trim() ? prev[tab].completed : false },
    }));
  };

  const updateCompleted = (tab: TabKey, completed: boolean) => {
    if (completed && !tabsData[tab].text.trim()) {
      setTextErrorTabs((prev) => (prev.includes(tab) ? prev : [...prev, tab]));
      return;
    }
    setTabsData((prev) => ({ ...prev, [tab]: { ...prev[tab], completed } }));
  };

  const [showValidation, setShowValidation] = useState(false);
  const [isPhotosLoading, setIsPhotosLoading] = useState(false);

  const incompleteTabs = TAB_KEYS.filter((k) => !tabsData[k].completed);

  const onError = (err: any) => openNotificationWithIcon("error", err.response?.data?.message || err.message);

  const buildFormBody = (): DebateReportForm =>
    TAB_KEYS.reduce((acc, tab) => {
      acc[TAB_SECTIONS[tab]] = {
        text: tabsData[tab].text,
        photos: tabsData[tab].bunchFiles.files.map((file) => file.filePath),
      };
      return acc;
    }, {} as DebateReportForm);

  // Загрузка формы и ссылки на PDF при открытии модалки.
  const { loading: isReportLoading } = useRequest(() => getDebateReport(debateId, countryIso), {
    ready: isOpen && !!localCountry?.iso && !!debateId,
    refreshDeps: [isOpen, debateId, countryIso],
    onSuccess: async (res) => {
      const sectionsWithPhotos = TAB_KEYS.map((tab) => ({ tab, section: res?.[TAB_SECTIONS[tab]] })).filter(
        ({ section }) => (section?.photos?.length ?? 0) > 0,
      );

      setTabsData((prev) => {
        const next = { ...prev };
        TAB_KEYS.forEach((tab) => {
          const section = res?.[TAB_SECTIONS[tab]];
          if (section?.text !== undefined) next[tab] = { ...next[tab], text: section.text ?? next[tab].text };
        });
        return next;
      });

      if (!sectionsWithPhotos.length) return;

      setIsPhotosLoading(true);
      try {
        const sectionsData = await Promise.all(
          sectionsWithPhotos.map(async ({ tab, section }) => {
            const photos = section?.photos ?? [];
            const files: FileDataType[] = photos.map((photo) => ({
              filePath: photo,
              originalName: decodeURIComponent(photo.split("/").pop() ?? photo),
            }));
            const fileList: UploadFile[] = await Promise.all(
              files.map(async (file, index) => {
                let url: string | undefined;
                try {
                  const blob = await getDebateReportPhoto(file.filePath, countryIso);
                  url = URL.createObjectURL(blob.data);
                } catch {
                  url = undefined;
                }
                return { uid: `${tab}-${index}`, name: file.originalName, status: "done", url } as UploadFile;
              }),
            );
            return { tab, files, fileList };
          }),
        );

        setTabsData((prev) => {
          const next = { ...prev };
          sectionsData.forEach(({ tab, files, fileList }) => {
            next[tab] = {
              ...next[tab],
              files: fileList,
              bunchFiles: { files, status: "DONE" },
            };
          });
          return next;
        });
      } finally {
        setIsPhotosLoading(false);
      }
    },
    onError,
  });

  const { run: saveReport, loading: isSaving } = useRequest(
    () => {
      const body = buildFormBody();
      return hasSavedRef.current
        ? updateDebateReport(debateId, body, countryIso)
        : createDebateReport(debateId, body, countryIso);
    },
    {
      manual: true,
      onSuccess: () => {
        hasSavedRef.current = true;
        openNotificationWithIcon("success", "Report saved");
        onClose();
      },
      onError,
    },
  );

  const { run: runGenerate, loading: isGenerating } = useRequest(
    () => generateDebateReport(debateId, buildFormBody(), countryIso),
    {
      manual: true,
      onSuccess: () => {
        openNotificationWithIcon("success", "Report generated");
        onClose();
      },
      onError,
    },
  );

  const handleGenerate = () => {
    if (incompleteTabs.length > 0) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    runGenerate();
  };

  const tabSetters = useMemo(
    () =>
      Object.fromEntries(
        TAB_KEYS.map((tab) => [
          tab,
          {
            setFileList: (action: SetStateAction<UploadFile[]>) =>
              setTabsData((prev) => ({
                ...prev,
                [tab]: {
                  ...prev[tab],
                  files: typeof action === "function" ? action(prev[tab].files) : action,
                },
              })),
            setBunchFiles: (action: SetStateAction<BunchFilesType>) =>
              setTabsData((prev) => ({
                ...prev,
                [tab]: {
                  ...prev[tab],
                  bunchFiles: typeof action === "function" ? action(prev[tab].bunchFiles) : action,
                },
              })),
          },
        ]),
      ) as Record<TabKey, TabSetters>,
    [],
  );

  return (
    <ModalComponentV1 className={styles.modal} isOpen={isOpen} onClose={onClose} title="Create Report">
      <div style={{ marginTop: 22 }}>
        <div className={styles.infoWrapper}>
          <div>Please fill in the information and attach photos if necessary</div>
          <div>The report will be sent once the Dispute is completed</div>
        </div>
        <div className={styles.segmentWrapper}>
          <CustomSegmented
            options={options}
            value={activeTab}
            onChange={(v) => {
              setActiveTab(Number(v) as TabKey);
              setShowValidation(false);
            }}
            style={{ maxWidth: "fit-content", marginRight: "0" }}
          />
        </div>
        {TAB_KEYS.map((tab) => (
          <div key={tab} style={{ display: activeTab === tab ? "block" : "none" }}>
            <CustomTextAreaV2
              additionalInfo={{ limit: 3000, title: "Additional information" }}
              style={{ minHeight: 153 }}
              placeholder="Collected essential data"
              value={tabsData[tab].text}
              status={textErrorTabs.includes(tab) ? "error" : undefined}
              loading={isReportLoading}
              onChange={(e) => updateText(tab, e.target.value)}
            />
            {textErrorTabs.includes(tab) && <div className={styles.textError}>Please fill in the text field</div>}
            <ReportUpload
              debateId={debateId}
              section={TAB_SECTIONS[tab]}
              fileList={tabsData[tab].files}
              setFileList={tabSetters[tab].setFileList}
              setBunchFiles={tabSetters[tab].setBunchFiles}
              loading={isPhotosLoading}
            />
            <div className={styles.completedWrapper}>
              <CustomSwitcher checked={tabsData[tab].completed} onChange={(checked) => updateCompleted(tab, checked)} />
              <span>Completed</span>
            </div>
          </div>
        ))}

        {showValidation && (
          <div className={styles.validationBanner}>
            <img src={warningIcon} alt="warning" />
            <span>Please complete the following sections: {incompleteTabs.map((k) => TAB_NAMES[k]).join(", ")}</span>
          </div>
        )}
        <div className={styles.buttonswrapper}>
          <CustomButton sizeButton="large" typeButton="tertiary" loading={isSaving} onClick={() => saveReport()}>
            Save & Close
          </CustomButton>
          <CustomButton sizeButton="large" typeButton="primary" loading={isGenerating} onClick={handleGenerate}>
            Generate Report
          </CustomButton>
        </div>
      </div>
    </ModalComponentV1>
  );
};
